import React, { useState } from 'react';
import { motion } from 'motion/react';
import '../style.css';
import {
  fadeUp,
  fadeLeft,
  fadeRight,
  staggerContainer,
  scaleFade,
  viewport,
} from '../components/useScrollAnimation';

/* ── DATA (Diambil dari Pamflet) ── */

const highlights = [
  {
    emoji: '📋',
    title: 'Persiapan & Pengecekan',
    desc: 'Bantuan persiapan dan pengecekan dokumen secara menyeluruh serta pendampingan proses aplikasi visa.',
  },
  {
    emoji: '🗣️',
    title: 'Persiapan Bahasa',
    desc: 'Bimbingan intensif persiapan IELTS / English Test untuk mencapai syarat Functional English minimal 4.5.',
  },
  {
    emoji: '💼',
    title: 'Kesiapan Kerja',
    desc: 'Bimbingan pembuatan CV berstandar Australia, Job Interview Preparation, dan Preparation for Working in Australia.',
  },
  {
    emoji: '🤝',
    title: 'Support Komprehensif',
    desc: 'Fasilitas Alumni Connection & Networking, Pre-Departure Preparation, dan berbagai support lainnya.',
  },
];

// *Catatan Web Designer: Pamflet tidak menyebutkan posisi spesifik, 
// jadi kita biarkan default posisi terpopuler untuk memancing minat, 
// namun banner di bawahnya kita ganti dengan "Estimasi Biaya".
const positions = [
  {
    badge: 'Hospitality',
    title: 'F&B dan Perhotelan',
    tagline: 'Sempurna untuk kamu yang ramah dan suka bertemu orang baru.',
    details: [{ label: 'Divisi', value: 'Barista, Waiter, Housekeeping' }]
  },
  {
    badge: 'Agriculture',
    title: 'Perkebunan & Pertanian',
    tagline: 'Gaji kompetitif, seringkali sudah termasuk akomodasi dari penyedia kerja.',
    details: [{ label: 'Divisi', value: 'Farm Hand, Fruit Picker, Packer' }]
  },
  {
    badge: 'Retail',
    title: 'Pusat Perbelanjaan',
    tagline: 'Cocok bagi yang memiliki kemampuan komunikasi bahasa Inggris yang baik.',
    details: [{ label: 'Divisi', value: 'Shop Assistant, Cashier, Stocker' }]
  }
];

const requirements = [
  {
    icon: 'fa-solid fa-passport',
    label: 'Kewarganegaraan',
    detail: 'Pemegang paspor Indonesia yang sah.',
  },
  {
    icon: 'fa-solid fa-user-check',
    label: 'Batas Usia',
    detail: 'Berusia 18 - 30 tahun saat pendaftaran.',
  },
  {
    icon: 'fa-solid fa-graduation-cap',
    label: 'Pendidikan',
    detail: 'Memenuhi persyaratan pendidikan WHV Australia.',
  },
  {
    icon: 'fa-solid fa-language',
    label: 'Functional English',
    detail: 'IELTS rata-rata minimal 4.5 atau bukti English lain yang memenuhi ketentuan.',
  },
  {
    icon: 'fa-solid fa-money-bill-wave',
    label: 'Bukti Finansial',
    detail: 'Memenuhi bukti dana sesuai ketentuan pemerintah Australia.',
  },
];

const steps = [
  { id: 1, title: 'Persiapan & Dokumen' },
  { id: 2, title: 'Registrasi Ballot' },
  { id: 3, title: 'Random Selection' },
  { id: 4, title: 'Jika Terpilih, Lanjut ke Aplikasi Visa' },
  { id: 5, title: 'Memenuhi Persyaratan Visa Australia' },
  { id: 6, title: 'Visa Granted' },
];

const masterClasses = [
  'Pembuatan CV Standar Australia',
  'Job Interview Preparation',
  'Preparation for Working in Australia',
  'Alumni Connection & Networking',
  'Pre-Departure Preparation'
];

const timeline = [
  { date: '2026', month: 'Later in', year: '', label: 'Perkiraan Registrasi Ballot Dibuka' },
  { date: 'TBA', month: '', year: '', label: 'Pengumuman Random Selection' },
  { date: 'TBA', month: '', year: '', label: 'Proses Aplikasi Visa Bagi yang Terpilih' },
];

/* ── COMPONENT ── */

const WorkingHolidayVisaSection = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <section id="whv-program" className="j1-section">

      {/* ── HERO ── */}
      <div className="j1-hero">
        <div className="j1-hero-overlay" />
        <div className="j1-hero-pattern" />

        <div className="container j1-hero-content">

          <motion.div
            className="j1-hero-eyebrow"
            variants={fadeLeft}
            initial="hidden"
            animate="show"
          >
            <span className="j1-badge" style={{ backgroundColor: '#dc3545' }}>NEW BALLOT SYSTEM!</span>
            <span className="j1-badge j1-badge-outline">Working Holiday Visa</span>
          </motion.div>

          <motion.h1
            className="j1-hero-headline"
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.15 }}
          >
            Kerja & Liburan di <span>Australia</span>
          </motion.h1>

          <motion.p
            className="j1-hero-sub"
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.28 }}
          >
            Punya mimpi tinggal, liburan sekaligus mendapatkan pengalaman kerja di Australia? Untuk first WHV, Australia kini memperkenalkan sistem BALLOT / RANDOM SELECTION bagi pemegang paspor Indonesia.
          </motion.p>

          <motion.div
            className="j1-hero-stats"
            variants={staggerContainer(0.12, 0.38)}
            initial="hidden"
            animate="show"
            style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: '30px' }}
          >
            <motion.span variants={scaleFade} className="j1-badge j1-badge-outline"><i className="fa-solid fa-passport"></i> Pemegang Paspor WNI</motion.span>
            <motion.span variants={scaleFade} className="j1-badge j1-badge-outline"><i className="fa-solid fa-user-clock"></i> Usia 18-30 Tahun</motion.span>
          </motion.div>

          <motion.div
            className="j1-hero-cta"
            variants={staggerContainer(0.14, 0.58)}
            initial="hidden"
            animate="show"
          >
            <motion.div variants={fadeUp}>
              <a href="https://wa.me/6281138914770" target="_blank" rel="noreferrer" className="j1-btn-primary">
                <i className="fa-brands fa-whatsapp" /> Mulai Persiapan Sekarang
              </a>
            </motion.div>
          </motion.div>

          <motion.p
            className="j1-hero-urgency"
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.75 }}
            style={{ marginTop: '20px', fontSize: '1rem', color: '#ffeb3b', fontWeight: 'bold' }}
          >
            <i className="fa-solid fa-bullhorn"></i> Jangan tunggu Ballot dibuka baru mulai persiapan!
          </motion.p>
        </div>
      </div>

      {/* ── STEPS / ALUR SINGKAT ── */}
      <div className="j1-steps-wrapper">
        <div className="j1-steps-bg-pattern" />
        <div className="container">
          <motion.div
            variants={staggerContainer(0.1, 0.1)}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
          >
            <div className="text-center mb-5">
              <span className="j1-section-label j1-section-label-light">Alur Singkat</span>
              <h2 className="j1-section-title j1-title-white">
                Sistem Ballot Terbaru
              </h2>
            </div>

            <div className="whv-stepper">
              {steps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <motion.div className="whv-step-node" variants={fadeUp}>
                    <div className="whv-step-icon">
                      <i className={[
                        'fa-solid fa-folder-open',
                        'fa-solid fa-pen-to-square',
                        'fa-solid fa-dice',
                        'fa-solid fa-paper-plane',
                        'fa-solid fa-shield-halved',
                        'fa-solid fa-stamp',
                      ][index]} />
                    </div>
                    <p className="whv-step-label">{step.title}</p>
                  </motion.div>
                  {index < steps.length - 1 && (
                    <div className="whv-step-arrow">
                      <i className="fa-solid fa-arrow-right" />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── KEY HIGHLIGHTS (NIEC PREPARATION PROGRAM) ── */}
      <div className="j1-highlights-wrapper">
        <div className="container">
          <motion.div
            className="j1-section-header"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
          >
            <span className="j1-section-label">NIEC WHV Preparation Program</span>
            <h2 className="j1-section-title">Kami Bantu Persiapanmu dari Awal Hingga Berangkat</h2>
            <p className="j1-section-desc" style={{ textAlign: 'left', maxWidth: '100%', margin: '16px 0' }}>
              Gunakan waktu sekarang untuk menyiapkan English, dokumen, CV dan kesiapan kerja sehingga saat kesempatan datang, kamu sudah lebih siap.
            </p>
          </motion.div>

          <motion.div
            className="j1-highlights-grid"
            variants={staggerContainer(0.12, 0.1)}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
          >
            {highlights.map((h, i) => (
              <motion.div key={i} className="j1-highlight-card" variants={scaleFade}>
                <div className="j1-highlight-emoji">{h.emoji}</div>
                <h3 className="j1-highlight-title">{h.title}</h3>
                <p className="j1-highlight-desc">{h.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* ── MEMBER EXCLUSIVE (PELATIHAN EXTRA) ── */}
      <div className="j1-member-wrapper">
        <div className="container">
          <div className="j1-member-inner">
            <motion.div className="j1-member-left" variants={fadeLeft} initial="hidden" whileInView="show" viewport={viewport}>
              <span className="j1-member-exclusive-badge">
                <i className="fa-solid fa-star" /> VALUE ADDED SERVICES
              </span>
              <h2 className="j1-section-title j1-title-white" style={{ marginTop: '20px' }}>
                Persiapan Kerja & Relasi
              </h2>
              <p className="j1-req-subtitle">
                Lebih dari sekadar agen visa. Kami memastikan kamu siap menghadapi dunia kerja di Australia melalui sesi pelatihan dan networking eksklusif.
              </p>
            </motion.div>

            <motion.div className="j1-member-right" variants={staggerContainer(0.08, 0.2)} initial="hidden" whileInView="show" viewport={viewport}>
              <div className="j1-masterclass-grid">
                {masterClasses.map((item, index) => (
                  <motion.div key={index} className="j1-masterclass-card" variants={scaleFade}>
                    <div className="j1-masterclass-num"><i className="fa-solid fa-check"></i></div>
                    <p className="j1-masterclass-title">{item}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* ── REQUIREMENTS & TIMELINE (IMPORTANT UPDATE) ── */}
      <div id="whv-requirements" className="j1-requirements-wrapper">
        <div className="container">
          <div className="j1-req-inner">
            {/* Left — Persyaratan */}
            <motion.div className="j1-req-left" variants={fadeLeft} initial="hidden" whileInView="show" viewport={viewport}>
              <span className="j1-section-label j1-section-label-light">Syarat Utama</span>
              <h2 className="j1-section-title j1-title-white">Persyaratan WHV</h2>
              <p className="j1-req-subtitle" style={{ color: '#ffc107', fontWeight: 'bold' }}>
                *Terpilih dalam ballot bukan berarti visa otomatis granted. Kamu tetap wajib memenuhi syarat di bawah ini:
              </p>
              <motion.div className="j1-req-grid" variants={staggerContainer(0.12, 0.2)} initial="hidden" whileInView="show" viewport={viewport}>
                {requirements.map((r, i) => (
                  <motion.div key={i} className="j1-req-card" variants={scaleFade}>
                    <div className="j1-req-icon"><i className={r.icon} /></div>
                    <div>
                      <p className="j1-req-label">{r.label}</p>
                      <p className="j1-req-detail">{r.detail}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right — Timeline / Update */}
            <motion.div className="j1-req-right" variants={fadeRight} initial="hidden" whileInView="show" viewport={viewport}>
              <span className="j1-section-label j1-section-label-light" style={{ backgroundColor: '#dc3545', color: '#fff', border: 'none' }}>Important Update</span>
              <h2 className="j1-section-title j1-title-white">Status Registrasi</h2>
              <p className="j1-req-subtitle">
                Ballot WHV untuk pemegang paspor Indonesia belum dibuka saat ini. Registrasi diperkirakan dibuka pada waktu berikut:
              </p>
              <motion.div className="j1-timeline-list" variants={staggerContainer(0.12, 0.2)} initial="hidden" whileInView="show" viewport={viewport}>
                {timeline.map((t, i) => (
                  <motion.div key={i} className="j1-timeline-item" variants={scaleFade}>
                    <div className="j1-timeline-date-block">
                      <span className="j1-timeline-date">{t.date}</span>
                      <span className="j1-timeline-month">{t.month} {t.year}</span>
                    </div>
                    <div className="j1-timeline-connector">
                      <div className="j1-timeline-dot" />
                      {i < timeline.length - 1 && <div className="j1-timeline-line" />}
                    </div>
                    <div className="j1-timeline-label">{t.label}</div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* ── POSITIONS & BIAYA ── */}
      <div id="whv-positions" className="j1-positions-wrapper">
        <div className="container">
          {/* <motion.div
            className="j1-section-header"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
          >
            <span className="j1-section-label">Peluang Karir</span>
            <h2 className="j1-section-title">Bidang Pekerjaan di Australia</h2>
          </motion.div> */}

          {/* <motion.div
            className="j1-tab-row"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
          >
            {positions.map((p, i) => (
              <button
                key={i}
                className={`j1-tab-btn${activeTab === i ? ' j1-tab-active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {p.badge}
              </button>
            ))}
          </motion.div> */}

          {/* <motion.div
            className="j1-positions-grid"
            variants={staggerContainer(0.18, 0.1)}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
          >
            {positions.map((p, i) => {
              const divisiVal = p.details?.find((d) => d.label === 'Divisi')?.value || p.badge;
              return (
                <motion.div
                  key={i}
                  className={`program-card program-card-revamped${activeTab === i ? ' program-card-active' : ''}`}
                  onClick={() => setActiveTab(i)}
                  variants={scaleFade}
                  style={{ display: activeTab === i ? 'block' : 'none' }}
                >
                  <h3 className="program-title">{p.title}</h3>
                  <div className="program-division-tag">
                    <i className="fa-solid fa-layer-group" /> Divisi: <strong>{divisiVal}</strong>
                  </div>
                  <p className="program-tagline">{p.tagline}</p>
                </motion.div>
              );
            })}
          </motion.div> */}

          {/* Specs Banner: ESTIMASI BIAYA (Diambil dari Pamflet) */}
          <motion.div
            className="j1-specs-banner"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            style={{ marginTop: '40px', padding: '30px', background: '#dc3545', color: '#fff', borderRadius: '12px' }}
          >
            <h3 style={{ margin: '0 0 20px 0', textAlign: 'center' }}>
              <i className="fa-solid fa-wallet"></i> Estimasi Biaya Program
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', maxWidth: '600px', margin: '0 auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.3)', paddingBottom: '10px' }}>
                <span>NIEC WHV Preparation & Administration</span>
                <strong>AUD 900</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.3)', paddingBottom: '10px' }}>
                <span>Visa Application Charge</span>
                <strong style={{ textAlign: 'right' }}>Mengikuti tarif resmi <br/>Australian Government</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Biaya Transaksi / Bank</span>
                <strong>$60</strong>
              </div>
            </div>
          </motion.div>

        </div>
      </div>

      {/* ── CTA ── */}
      <div id="whv-register" className="j1-cta-wrapper">
        <div className="container">
          <motion.div className="j1-cta-inner" variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport}>
            <span className="j1-section-label">Dream • Act • Succeed</span>
            <h2 className="j1-cta-title">
              Ambil Langkah Pertamamu Sekarang!
            </h2>
            <p className="j1-cta-desc">
              Gunakan waktu sekarang untuk menyiapkan English, dokumen, CV, dan kesiapan kerjamu. Konsultasikan profilmu dengan tim NIEC hari ini secara gratis.
            </p>
            <motion.div className="j1-cta-actions" variants={staggerContainer(0.15, 0.2)} initial="hidden" whileInView="show" viewport={viewport}>
              <motion.a href="https://wa.me/6281138914770" target="_blank" rel="noreferrer" className="j1-btn-primary j1-btn-lg" variants={scaleFade}>
                <i className="fa-brands fa-whatsapp" /> Konsultasi & Cek Dokumen
              </motion.a>
              <motion.a href="/#register" className="j1-btn-outline j1-btn-lg" variants={scaleFade}>
                Daftar Sekarang
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </div>

    </section>
  );
};

export default WorkingHolidayVisaSection;